import React, { useEffect, useState } from 'react';
import { useNavigate, useParams, useLocation, Link } from 'react-router-dom';
import { ArrowLeft, CheckCircle2, ExternalLink, Hash, RefreshCw, ScrollText } from 'lucide-react';
import { Button } from '@components/Button';
import { Card, CardContent, CardHeader } from '@components/Card';
import { ConfirmDialog } from '@components/Dialog';
import { PermissionGuard } from '@components/PermissionGuard';
import { invoiceInvalidationService } from '@services/invoiceInvalidationService';
import { InvoiceInvalidation } from '@/types/invoiceInvalidation';
import { getInvoiceModelLabel } from '@/types/invoice';
import { formatCnpj } from '@/types/company';
import { getInvalidationStatusBadge } from './statusBadge';

const formatDate = (iso?: string | null) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'medium' });
  } catch {
    return iso;
  }
};

export const InvoiceInvalidationDetailView: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const location = useLocation();
  const successMessage = (location.state as { successMessage?: string } | null)?.successMessage;

  const [item, setItem] = useState<InvoiceInvalidation | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [retryConfirmOpen, setRetryConfirmOpen] = useState(false);
  const [retrying, setRetrying] = useState(false);
  const [retryError, setRetryError] = useState<string | null>(null);

  useEffect(() => {
    if (!id) return;
    setLoading(true);
    setError(null);
    invoiceInvalidationService
      .getById(Number(id))
      .then(setItem)
      .catch((err) => setError(err instanceof Error ? err.message : 'Falha ao carregar invalidação'))
      .finally(() => setLoading(false));
  }, [id]);

  return (
    <PermissionGuard
      permission="invoice_invalidation.read"
      fallback={
        <Card>
          <CardContent>
            <p className="text-slate-600">Você não tem permissão para visualizar invalidações.</p>
          </CardContent>
        </Card>
      }
    >
      <div className="space-y-6">
        <div className="flex items-center gap-4">
          <Button
            variant="secondary"
            onClick={() => navigate('/settings/fiscal/invoice-invalidations')}
          >
            <ArrowLeft size={20} />
          </Button>
          <div>
            <h1 className="text-h1">Invalidação #{id}</h1>
            <p className="text-slate-600">Detalhe da invalidação submetida à SEFAZ.</p>
          </div>
        </div>

        {successMessage && (
          <Card className="border-emerald-200 bg-emerald-50">
            <CardContent>
              <div className="flex items-center gap-2 text-emerald-700">
                <CheckCircle2 size={18} />
                <p>{successMessage}</p>
              </div>
            </CardContent>
          </Card>
        )}

        {error && (
          <Card className="border-red-200 bg-red-50">
            <CardContent>
              <p className="text-red-600">{error}</p>
            </CardContent>
          </Card>
        )}

        {loading ? (
          <Card>
            <CardContent>
              <div className="space-y-3">
                {[...Array(6)].map((_, i) => (
                  <div key={i} className="h-6 animate-pulse rounded bg-slate-200" />
                ))}
              </div>
            </CardContent>
          </Card>
        ) : item ? (
          <>
            <Card>
              <CardHeader title="Resumo" />
              <CardContent>
                <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
                  <Field label="Empresa" value={item.company_name || `Empresa #${item.company_id}`} />
                  <Field
                    label="CNPJ"
                    value={item.company_document ? formatCnpj(item.company_document) : '—'}
                  />
                  <Field
                    label="Status"
                    value={
                      <span
                        className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                          getInvalidationStatusBadge(item.status_id, item.status_name).className
                        }`}
                      >
                        {getInvalidationStatusBadge(item.status_id, item.status_name).label}
                      </span>
                    }
                  />
                  <Field label="Modelo" value={getInvoiceModelLabel(item.model)} />
                  <Field label="Série" value={item.serie} />
                  <Field
                    label="Faixa"
                    value={
                      <span className="font-mono">
                        {item.number_start} — {item.number_end}{' '}
                        <span className="text-xs text-slate-500">
                          ({item.number_end - item.number_start + 1} números)
                        </span>
                      </span>
                    }
                  />
                  <Field
                    label="Protocolo SEFAZ"
                    value={
                      item.protocol_number ? (
                        <span className="inline-flex items-center gap-1 font-mono">
                          <Hash size={14} className="text-slate-400" />
                          {item.protocol_number}
                        </span>
                      ) : (
                        '—'
                      )
                    }
                  />
                  <Field label="Criado em" value={formatDate(item.created_at)} />
                  <Field label="Atualizado em" value={formatDate(item.updated_at)} />
                  <Field label="Criado por" value={item.created_by || '—'} />
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader title="Justificativa" />
              <CardContent>
                <p className="whitespace-pre-wrap text-sm text-slate-700">{item.justification}</p>
              </CardContent>
            </Card>

            {item.sefaz_log_id && (
              <Card>
                <CardHeader title="Comunicação SEFAZ" />
                <CardContent>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2 text-slate-700">
                      <ScrollText size={18} className="text-slate-500" />
                      <span>Log de comunicação: #{item.sefaz_log_id}</span>
                    </div>
                    <Link
                      to={`/settings/fiscal/sefaz-communication-logs/${item.sefaz_log_id}`}
                      className="inline-flex items-center gap-1 text-emerald-600 hover:underline text-sm"
                    >
                      Ver log <ExternalLink size={14} />
                    </Link>
                  </div>
                </CardContent>
              </Card>
            )}

            {item.status_id === 3 && (
              <Card className="border-amber-200 bg-amber-50">
                <CardContent>
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-medium text-amber-800">Invalidação rejeitada pela SEFAZ</p>
                      <p className="text-sm text-amber-700 mt-1">
                        É possível reenviar esta invalidação para uma nova tentativa de homologação.
                      </p>
                    </div>
                    <Button
                      variant="primary"
                      onClick={() => setRetryConfirmOpen(true)}
                      disabled={retrying}
                    >
                      <RefreshCw size={16} className={retrying ? 'animate-spin' : ''} />
                      Reenviar
                    </Button>
                  </div>
                  {retryError && (
                    <p className="mt-2 text-sm text-red-600">{retryError}</p>
                  )}
                </CardContent>
              </Card>
            )}

            <ConfirmDialog
              isOpen={retryConfirmOpen}
              onClose={() => setRetryConfirmOpen(false)}
              onConfirm={async () => {
                setRetrying(true);
                setRetryError(null);
                try {
                  const updated = await invoiceInvalidationService.retry(item.invalidation_id);
                  setItem(updated);
                  setRetryConfirmOpen(false);
                } catch (err) {
                  setRetryError(err instanceof Error ? err.message : 'Falha ao reenviar invalidação');
                  setRetryConfirmOpen(false);
                } finally {
                  setRetrying(false);
                }
              }}
              severity="danger"
              loading={retrying}
              title="Reenviar invalidação ao SEFAZ"
              message={`Deseja reenviar esta invalidação (faixa ${item.number_start}–${item.number_end}, série ${item.serie}) ao SEFAZ? Uma nova comunicação será registrada.`}
            />
          </>
        ) : null}
      </div>
    </PermissionGuard>
  );
};

const Field: React.FC<{ label: string; value: React.ReactNode }> = ({ label, value }) => (
  <div>
    <p className="text-xs uppercase tracking-wider text-slate-500 font-medium">{label}</p>
    <p className="text-slate-800 text-sm mt-0.5">{value ?? '—'}</p>
  </div>
);
